/**
 * Nova Store
 * 
 * Centralized state management for Nova components using a custom hook pattern.
 * This store handles all state and actions related to Nova data, queries, and UI.
 * The engine is used as the source of truth for schema information.
 */

import * as React from 'react';
import { 
  Engine, 
  MaterializedTableData, 
  Query, 
  QueryTypeEnum as QueryType,
  SQLDialect,
  TableRef,
  TableView
} from '@/lib/engine';
import { Column, ColumnRef } from '../data-grid/types';
import { toast } from '@/components/ui';

// Status enum for more explicit states
type NovaStatus = 'idle' | 'loading' | 'error' | 'success';

/**
 * Public interface for Nova Store
 * This is what components can access
 */
export interface NovaStore {
  // Data access - read only
  readonly activeTable: TableRef | null;
  readonly data: MaterializedTableData | null;
  readonly columns: Column[];
  readonly columnRefs: ColumnRef[];
  readonly originalColumnRefs: ColumnRef[];  // Schema columns from engine

  // Query state - read only
  readonly query: Query;
  readonly lastExecutedQuery: Query | null;
  readonly lastExecutedView: TableView | null;

  // UI state - read only
  readonly status: NovaStatus;
  readonly error: Error | null;
  readonly isQueryBuilderExpanded: boolean;
  readonly lastExpandedSize: number;
  readonly panelWidths: Record<'datasets' | 'center' | 'chat', number>;

  // Actions - methods to modify state
  executeQuery: (query: Query) => Promise<void>;
  updateQuery: (query: Query) => void;
  setActiveTable: (table: TableRef) => Promise<void>;
  setQueryBuilderExpanded: (expanded: boolean) => void;
  setLastExpandedSize: (size: number) => void;
  setPanelWidth: (panel: 'datasets' | 'center' | 'chat', width: number) => void;
  runSqlQuery: (sqlQuery: string) => void;
  logState: () => void;
}

/**
 * Internal state structure - private to the store
 * Engine is source of truth for schema information
 */
interface NovaState {
  // Core state - minimal, no schema duplication
  table: TableRef | null;
  
  // Query state
  query: Query;  // Current query
  queryResult: {
    query: Query | null;  // Last executed
    view: TableView | null;
    data: MaterializedTableData | null;
  };

  // Display state - only UI specific data
  displayColumns: {
    widths: Record<string, number>;
    order: string[];
  };

  // UI state
  ui: {
    status: NovaStatus;
    error: Error | null;
    isQueryBuilderExpanded: boolean;
    lastExpandedSize: number;
    panelWidths: Record<'datasets' | 'center' | 'chat', number>;
  };
}

// Store Options Interface
export interface UseNovaStoreOptions {
  engine: Engine;
}

// Default panel sizes
const DEFAULT_EXPANDED_SIZE = 30;
const MIN_EXPANDED_SIZE = 20;
const MAX_EXPANDED_SIZE = 70;

// Add a new type for tracking active operations
interface PendingOperation {
  id: number;
  type: 'setActiveTable' | 'executeQuery';
}

/**
 * Helper function to create display columns from column refs
 */
function createDisplayColumns(
  columnRefs: ColumnRef[], 
  displayState: { widths: Record<string, number>; order: string[] }
): Column[] {
  return columnRefs.map(ref => ({
    name: ref.name,
    header: ref.name,
    type: ref.schema.type,
    nullable: ref.schema.nullable,
    sortable: true,
    width: displayState.widths[ref.name] || 150
  }));
}

/**
 * Nova Store Hook
 * 
 * Custom hook that provides state and actions for Nova components.
 * Uses engine as source of truth for schema information.
 */
export function useNovaStore({ engine }: UseNovaStoreOptions): NovaStore {
  // Get initial expanded size from localStorage
  const initialExpandedSize = React.useMemo(() => {
    try {
      const savedSize = localStorage.getItem('nova-query-panel-size');
      if (savedSize) {
        const parsedSize = parseInt(savedSize, 10);
        if (!isNaN(parsedSize) && parsedSize >= MIN_EXPANDED_SIZE && parsedSize <= MAX_EXPANDED_SIZE) {
          return parsedSize;
        }
      }
    } catch (e) {
      console.warn('Failed to restore saved panel size:', e);
    }
    return DEFAULT_EXPANDED_SIZE;
  }, []);

  // Internal state
  const [state, setState] = React.useState<NovaState>({
    table: null,
    query: {
      type: QueryType.SELECT,
      options: {
        columns: [],
        where: "",
        orderBy: []
      }
    },
    queryResult: {
      query: null,
      view: null,
      data: null
    },
    displayColumns: {
      widths: {},
      order: []
    },
    ui: {
      status: 'idle',
      error: null,
      isQueryBuilderExpanded: false,
      lastExpandedSize: initialExpandedSize,
      panelWidths: {
        datasets: 0,
        center: 0,
        chat: 0
      }
    }
  });

  // Cache for column information
  const columnCache = React.useRef<{
    table: TableRef | null;
    columns: ColumnRef[] | null;
    promise: Promise<ColumnRef[]> | null;
  }>({
    table: null,
    columns: null,
    promise: null
  });

  // Track the latest operation to prevent race conditions
  const latestOperation = React.useRef<PendingOperation>({
    id: 0,
    type: 'setActiveTable'
  });

  /**
   * Get columns from engine with caching
   */
  const getColumns = React.useCallback(async (table: TableRef): Promise<ColumnRef[]> => {
    if (!table) return [];

    // Check cache
    if (columnCache.current.table?.id === table.id && columnCache.current.columns) {
      return columnCache.current.columns;
    }

    // Check if we're already fetching
    if (columnCache.current.table?.id === table.id && columnCache.current.promise) {
      return columnCache.current.promise;
    }

    // Fetch columns
    const promise = (async () => {
      try {
        // Get table view
        const view = await engine.getTableView(table);
        
        // Execute simple query to get schema
        const schemaQuery = {
          type: QueryType.SQL,
          options: {
            sql: `SELECT * FROM "${table.path}" LIMIT 1`,
            dialect: SQLDialect.DUCKDB
          }
        };
        
        const transformed = await engine.transform(view, schemaQuery);
        const result = await engine.materialize(transformed);
        
        if (!result.table?.schema?.fields) {
          throw new Error("Failed to get table schema");
        }

        // Create column refs
        const columns = result.table.schema.fields.map(field => ({
          name: field.name,
          schema: {
            name: field.name,
            type: field.type?.toString() || 'string',
            nullable: true
          }
        }));

        // Update cache
        columnCache.current = {
          table,
          columns,
          promise: null
        };

        return columns;
      } catch (error) {
        columnCache.current.promise = null;
        throw error;
      }
    })();

    columnCache.current.promise = promise;
    return promise;
  }, [engine]);

  /**
   * Set the active table and execute default query
   */
  const setActiveTable = React.useCallback(async (table: TableRef) => {
    try {
      if (!table || !table.id) {
        throw new Error("Invalid table reference");
      }

      // Only set loading status initially - keep existing data visible if any
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, status: 'loading', error: null }
      }));

      // Get all required data before any state updates
      const columns = await getColumns(table);

      // Create the initial query with all columns
      const query: Query = {
        type: QueryType.SELECT,
        options: {
          columns,
          where: "",
          orderBy: []
        }
      };

      // Execute all async operations and wait for completion
      const [view, transformed, result] = await Promise.all([
        engine.getTableView(table),
        engine.getTableView(table).then(v => engine.transform(v, query)),
        engine.getTableView(table).then(v => engine.transform(v, query)).then(t => engine.materialize(t))
      ]);

      // Validate that we have all required data before updating state
      if (!columns || columns.length === 0) {
        throw new Error("No columns available for table");
      }
      
      if (!result || !result.table) {
        throw new Error("Failed to materialize table data");
      }

      // Update column cache atomically
      columnCache.current = {
        table,
        columns,
        promise: null
      };

      // Single atomic update with all state changes - only transition to 'success' when everything is ready
      setState(prev => ({
        ...prev,
        table,
        query,
        queryResult: {
          query,
          view: transformed,
          data: result
        },
        displayColumns: {
          widths: {},
          order: columns.map(c => c.name)
        },
        ui: { 
          ...prev.ui, 
          status: 'success',
          error: null 
        }
      }));

      // Save table for future sessions
      try {
        localStorage.setItem('app-active-table', JSON.stringify({
          id: table.id,
          path: table.path
        }));
      } catch (e) {
        console.warn('Failed to save active table to localStorage:', e);
      }
    } catch (error) {
      console.error('Error setting active table:', error);
      
      // Reset all state atomically on error
      setState(prev => ({ 
        ...prev,
        table: null,
        query: {
          type: QueryType.SELECT,
          options: {
            columns: [],
            where: "",
            orderBy: []
          }
        },
        queryResult: {
          query: null,
          view: null,
          data: null
        },
        displayColumns: {
          widths: {},
          order: []
        },
        ui: { 
          ...prev.ui, 
          status: 'error', 
          error: error instanceof Error ? error : new Error(String(error)) 
        }
      }));

      // Reset column cache atomically
      columnCache.current = {
        table: null,
        columns: null,
        promise: null
      };
    }
  }, [engine, getColumns]);

  /**
   * Execute query and update state with results
   */
  const executeQuery = React.useCallback(async (queryToExecute: Query) => {
    if (!state.table) {
      console.warn('Cannot execute query without active table');
      return;
    }

    // Set loading state but keep previous data
    setState(prev => ({
      ...prev,
      ui: { ...prev.ui, status: 'loading', error: null }
    }));

    try {
      // Execute all query operations before any state updates
      const view = await engine.getTableView(state.table);
      const transformed = await engine.transform(view, queryToExecute);
      const result = await engine.materialize(transformed);

      // Single atomic update for all query-related state
      setState(prev => ({
        ...prev,
        query: queryToExecute,
        queryResult: {
          query: queryToExecute,
          view: transformed,
          data: result
        },
        ui: { ...prev.ui, status: 'success', error: null }
      }));
    } catch (error) {
      console.error('Error executing query:', error);
      
      // On error, keep the previous data but show error state
      setState(prev => ({ 
        ...prev,
        query: queryToExecute,
        queryResult: {
          ...prev.queryResult, // Keep previous data and view
          query: null
        },
        ui: { 
          ...prev.ui, 
          status: 'error', 
          error: error instanceof Error ? error : new Error(String(error)) 
        }
      }));
    }
  }, [engine, state.table]);

  /**
   * Update query without executing
   * Keeps previous data until new data is available
   */
  const updateQuery = React.useCallback((newQuery: Query) => {
    // Update query but keep previous data and results
    setState(prev => ({ 
      ...prev, 
      query: newQuery,
      // Keep previous data and view until new data is available
      queryResult: {
        ...prev.queryResult,
        query: null, // Only clear the query reference
      },
      ui: { ...prev.ui, status: 'idle' }
    }));
  }, []);

  /**
   * Run SQL query
   */
  const runSqlQuery = React.useCallback((sqlQuery: string) => {
    const query: Query = {
      type: QueryType.SQL,
      options: {
        sql: sqlQuery,
        dialect: SQLDialect.DUCKDB
      }
    };
    executeQuery(query);
  }, [executeQuery]);

  /**
   * UI state management
   */
  const setQueryBuilderExpanded = React.useCallback((expanded: boolean) => {
    setState(prev => ({ 
      ...prev, 
      ui: { ...prev.ui, isQueryBuilderExpanded: expanded }
    }));
  }, []);

  const setLastExpandedSize = React.useCallback((size: number) => {
    const validSize = Math.max(MIN_EXPANDED_SIZE, Math.min(size, MAX_EXPANDED_SIZE));
    setState(prev => ({ 
      ...prev, 
      ui: { ...prev.ui, lastExpandedSize: validSize }
    }));
    try {
      localStorage.setItem('nova-query-panel-size', String(validSize));
    } catch (e) {
      console.warn('Failed to save panel size to localStorage:', e);
    }
  }, []);

  const setPanelWidth = React.useCallback((panel: 'datasets' | 'center' | 'chat', width: number) => {
    setState(prev => ({
      ...prev,
      ui: {
        ...prev.ui,
        panelWidths: {
          ...prev.ui.panelWidths,
          [panel]: width
        }
      }
    }));
  }, []);

  const logState = React.useCallback(() => {
    console.log("Nova Store State:", {
      activeTable: state.table,
      query: state.query,
      status: state.ui.status
    });
  }, [state]);

  // Initialize store
  React.useEffect(() => {
    let isMounted = true;
    
    const initialize = async () => {
      if (!isMounted) return;
      
      try {
        if (!engine) {
          throw new Error("Engine is not initialized");
        }
        
        // List tables but don't change status - let setActiveTable handle status changes
        await engine.listRegisteredTables();
        
        // Don't set status to 'success' here - let the parent component handle table activation
        // The status should remain 'idle' until a table is actually set and loaded
      } catch (error) {
        console.error("Failed to initialize store:", error);
        if (isMounted) {
          setState(prev => ({ 
            ...prev, 
            ui: { ...prev.ui, status: 'error', error: error instanceof Error ? error : new Error(String(error)) }
          }));
        }
      }
    };
    
    initialize();
    
    return () => {
      isMounted = false;
    };
  }, [engine]);

  // Public interface
  return {
    // Data access - read only
    get activeTable() { return state.table; },
    get data() { return state.queryResult.data; },
    get columns() {
      if (!state.table) return [];
      const columnRefs = columnCache.current.columns || [];
      return createDisplayColumns(columnRefs, state.displayColumns);
    },
    get columnRefs() {
      return columnCache.current.columns || [];
    },
    get originalColumnRefs() {
      return columnCache.current.columns || [];
    },

    // Query state - read only
    get query() { return state.query; },
    get lastExecutedQuery() { return state.queryResult.query; },
    get lastExecutedView() { return state.queryResult.view; },

    // UI state - read only
    get status() { return state.ui.status; },
    get error() { return state.ui.error; },
    get isQueryBuilderExpanded() { return state.ui.isQueryBuilderExpanded; },
    get lastExpandedSize() { return state.ui.lastExpandedSize; },
    get panelWidths() { return state.ui.panelWidths; },

    // Actions - methods to modify state
    executeQuery,
    updateQuery,
    setActiveTable,
    setQueryBuilderExpanded,
    setLastExpandedSize,
    setPanelWidth,
    runSqlQuery,
    logState
  };
} 